// Retry decision for widget HTTP requests, kept free of store imports so it can
// be unit tested (the client itself pulls in the session store).

export const MAX_RETRIES = 3;
export const RETRY_BASE_DELAY = 3000;

interface RetryDecision {
  // 0 / undefined for network errors and timeouts — the request may still have
  // reached the server, we just stopped waiting for the response.
  status?: number;
  retryCount: number;
  // `false` opts out entirely: set for requests that are not safe to repeat.
  retry?: boolean;
}

export const shouldRetryRequest = ({
  status,
  retryCount,
  retry,
}: RetryDecision): boolean => {
  if (retry === false) {
    return false;
  }

  if (retryCount >= MAX_RETRIES) {
    return false;
  }

  // Network error or timeout.
  if (!status) {
    return true;
  }

  // 5xx for internal server errors, 408 for timeout, 429 for too many requests.
  // Note 409 is deliberately absent even though the API also returns it for
  // unhandled server errors — retrying a conflict is not safe in general.
  return status >= 500 || status === 408 || status === 429;
};

// Exponential backoff with base 3: delay triples with each retry.
export const retryDelayFor = (retryCount: number): number =>
  RETRY_BASE_DELAY * Math.pow(3, retryCount);
